import ControlledCalibrationBatch from "./ControlledCalibrationBatch";
import { useState } from "react";
import { tipsRequest } from "../helpers/tipsWorkflow";
import {
  analyzeLeagueBatch,
  saveLeagueBatch,
  resetLeagueBatch,
  recoverLeagueBatch,
} from "../helpers/leagueAnalysisBatch";
export default function LeagueAnalysisBatch({
  catalog,
  profile,
  market,
  language,
  run,
  onChange,
  onOpen,
  disabled,
  onBusy,
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const en = language?.startsWith("en");
  const text = (de, english) => (en ? english : de);
  const request = (body) => tipsRequest("league-analysis", body);
  const act = async (fn) => {
    setBusy(true);
    onBusy?.(true);
    setError("");
    try {
      onChange(await fn());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      onBusy?.(false);
    }
  };
  const ready = run?.items.filter((i) => i.status === "ready") || [],
    saved =
      run?.items.filter((i) => i.status === "saved" && i.saved?.snapshot) || [];
  const locked = busy || disabled,
    fmt = (x) =>
      Number.isFinite(x)
        ? x.toLocaleString(en ? "en" : "de", { maximumFractionDigits: 3 })
        : "—";
  const save = (selection) => {
    if (
      window.confirm(
        text(
          "Ausgewählte Entwürfe bewusst speichern? Team Settings und Saisonparameter werden gemeinsam gesichert. Keine Re-Compilation.",
          "Save selected drafts? Team settings and season parameters are backed up together. No recompilation."
        )
      )
    )
      act(() => saveLeagueBatch(run, request, selection, onChange));
  };
  return (
    <details className="la-batch">
      <summary>
        {text(
          "Admin: ligaweite Entwürfe / Reset",
          "Admin: league-wide drafts / reset"
        )}
      </summary>
      <ControlledCalibrationBatch
        profile={profile}
        market={market}
        language={language}
        disabled={locked}
        onBusy={onBusy}
      />
      <p>
        {text(
          "Verwendet das gewählte Analyseprofil. Deterministische Berechnung ohne OpenAI-Aufrufe. Serie A bleibt bis zur eindeutigen Baseline-Prüfung ausgenommen.",
          "Uses the selected analysis profile. Deterministic calculation without OpenAI calls. Serie A remains excluded until its baseline is verified."
        )}
      </p>
      <button
        disabled={locked}
        onClick={() =>
          act(() =>
            analyzeLeagueBatch(
              catalog,
              request,
              {
                id: `batch-${window.crypto?.randomUUID?.() || Date.now()}`,
                profile,
                market_orientation: market,
                language: en ? "en" : "de",
              },
              onChange
            )
          )
        }
      >
        {text("Alle Ligen analysieren", "Analyze all leagues")}
      </button>{" "}
      <button
        disabled={locked}
        onClick={() => act(() => recoverLeagueBatch(catalog, request))}
      >
        {text("Letzten gespeicherten Lauf laden", "Load last saved batch")}
      </button>
      {error && <p role="alert">{error}</p>}
      {run && (
        <>
          <p role="status">
            {run.phase} · {run.completed ?? run.items.length}/
            {run.total ?? run.items.length} · {run.id}
          </p>
          <div className="le-scroll">
            <table>
              <thead>
                <tr>
                  {[
                    text("Liga", "League"),
                    text("Teams", "Teams"),
                    text("Änderungen", "Changes"),
                    text("Stärke max.", "Strength max."),
                    text("Goal Sum max.", "Goal sum max."),
                    "Δ Goals",
                    "Δ Home",
                    "Δ Draw",
                    text("Auffällig", "Flagged"),
                    "Status",
                    text("Aktionen", "Actions"),
                  ].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {run.items.map((item) => (
                  <tr key={`${item.league_id}:${item.season_id}`}>
                    <td>{item.league_name}</td>
                    <td>
                      {item.summary?.teams ?? item.context?.teams.length ?? "—"}
                    </td>
                    <td>{item.summary?.changed_fields ?? "—"}</td>
                    <td>
                      {fmt(item.summary?.largest_strength.strength * 100)} %
                    </td>
                    <td>{fmt(item.summary?.largest_goals.goals * 100)} %</td>
                    {[
                      "stored_goal_average",
                      "exp_home_factor",
                      "drawfactor",
                    ].map((k) => (
                      <td key={k}>{fmt(item.summary?.parameters[k].delta)}</td>
                    ))}
                    <td>
                      {item.summary?.outliers
                        ? `⚠ ${item.summary.outliers}`
                        : item.summary
                        ? "0"
                        : "—"}
                    </td>
                    <td>
                      {item.status}
                      {item.error ? `: ${item.error}` : ""}
                    </td>
                    <td>
                      {item.draft && (
                        <button
                          disabled={locked}
                          onClick={() => onOpen(item, run.id)}
                        >
                          {text("Liga ansehen", "View league")}
                        </button>
                      )}
                      {item.status === "ready" && (
                        <>
                          <button
                            disabled={locked}
                            onClick={() => save([item.league_id])}
                          >
                            {text("Liga übernehmen", "Apply league")}
                          </button>
                          <button
                            disabled={locked}
                            onClick={() =>
                              onChange({
                                ...run,
                                items: run.items.map((i) =>
                                  i === item
                                    ? {
                                        ...i,
                                        status: "skipped",
                                        error: "SKIPPED_BY_ADMIN",
                                      }
                                    : i
                                ),
                              })
                            }
                          >
                            {text("Überspringen", "Skip")}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            disabled={locked || !ready.some((i) => !i.summary.outliers)}
            onClick={() => save("unremarkable")}
          >
            {text("Alle unauffälligen übernehmen", "Apply unflagged leagues")}
          </button>{" "}
          <button
            disabled={locked || !ready.length}
            onClick={() => save("all")}
          >
            {text("Alle Entwürfe übernehmen", "Apply all drafts")}
          </button>{" "}
          <button
            disabled={
              locked ||
              !saved.length ||
              saved.some((i) => !i.snapshot?.complete)
            }
            onClick={() => {
              if (
                window.confirm(
                  text(
                    "Den gesamten gespeicherten Lauf zurücksetzen? Zwischenzeitlich veränderte Settings werden nicht überschrieben.",
                    "Reset the whole saved batch? Settings changed in the meantime will not be overwritten."
                  )
                )
              )
                act(() => resetLeagueBatch(run, request, onChange));
            }}
          >
            {text(
              "Alle Ligen auf vorherigen Stand zurücksetzen",
              "Restore all leagues to previous state"
            )}
          </button>
          <p>
            {text(
              "Auffällig: Stärkeänderung über 25 % oder Goal Sum über 10 %. Dies ist nur eine Prüfmarkierung, keine neue Settings-Formel. Reset erfolgt atomar je Liga; Unterbrechungen werden als Teilstand angezeigt und sind über den gespeicherten Lauf wiederaufnehmbar.",
              "Flagged: strength change above 25% or goal sum above 10%. Review markers only, not a new settings formula. Reset is atomic per league; interrupted runs show partial status and can be recovered from saved snapshots."
            )}
          </p>
        </>
      )}
    </details>
  );
}
